import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { ScrollableContainer } from '../../../components/ui/ScrollableContainer';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiRefreshCw } from 'react-icons/fi';
import { calculateProfitLoss, ProfitLossData } from '../service/profitLossService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
  align-items: center;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${({ active }) =>
    active ? theme.colors.primary[500] : theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  background-color: ${({ active }) =>
    active ? theme.colors.primary[500] : theme.colors.white};
  color: ${({ active }) =>
    active ? theme.colors.white : theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active }) =>
      active ? theme.colors.primary[600] : theme.colors.light.background.secondary};
    border-color: ${({ active }) =>
      active ? theme.colors.primary[600] : theme.colors.light.border.medium};
  }
`;

const SelectContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const Select = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ReportCard = styled(Card)`
  margin-bottom: ${theme.spacing[6]};
`;

const ReportTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ReportRow = styled.tr<{ isTotal?: boolean }>`
  border-bottom: 1px solid ${theme.colors.light.border.light};
  
  ${({ isTotal }) => isTotal && `
    border-top: 2px solid ${theme.colors.light.border.medium};
    border-bottom: 2px solid ${theme.colors.light.border.medium};
    font-weight: ${theme.typography.fontWeight.bold};
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

const ReportCell = styled.td<{ align?: 'left' | 'right' }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: ${({ align }) => align || 'left'};
  color: ${theme.colors.light.text.primary};
  
  &:first-child {
    font-weight: ${theme.typography.fontWeight.medium};
  }
`;

const ReportHeader = styled.th<{ align?: 'left' | 'right' }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: ${({ align }) => align || 'left'};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  border-bottom: 2px solid ${theme.colors.light.border.medium};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.light.text.secondary};
`;

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const ProfitLossPage: React.FC = () => {
  const { user, companyData } = useAuth();
  const [periodType, setPeriodType] = useState<'month' | 'half-year' | 'full-year'>('month');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedHalf, setSelectedHalf] = useState<'H1' | 'H2'>('H1');
  const [profitLossData, setProfitLossData] = useState<ProfitLossData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const companyId = companyData?.id || user?.uid || '';

  useEffect(() => {
    if (companyId) {
      loadProfitLoss();
    }
  }, [companyId, periodType, selectedMonth, selectedYear, selectedHalf]);

  const loadProfitLoss = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const monthForHalfYear = periodType === 'half-year' 
        ? (selectedHalf === 'H1' ? 0 : 6) // H1 starts at month 0 (Jan), H2 starts at month 6 (Jul)
        : selectedMonth;
      
      const data = await calculateProfitLoss(
        companyId,
        periodType,
        periodType === 'month' ? selectedMonth : monthForHalfYear,
        selectedYear
      );
      setProfitLossData(data);
    } catch (error: any) {
      toast.error('Failed to load profit and loss data: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <DashboardLayout>
      <ScrollableContainer>
        <PageHeader>
          <PageTitle>Profit & Loss Statement</PageTitle>
          <Button onClick={loadProfitLoss} disabled={loading}>
            <FiRefreshCw style={{ marginRight: theme.spacing[2] }} />
            Refresh
          </Button>
        </PageHeader>

        <FilterContainer>
        <FilterButton
          active={periodType === 'month'}
          onClick={() => setPeriodType('month')}
        >
          Month
        </FilterButton>
        <FilterButton
          active={periodType === 'half-year'}
          onClick={() => setPeriodType('half-year')}
        >
          Half Year
        </FilterButton>
        <FilterButton
          active={periodType === 'full-year'}
          onClick={() => setPeriodType('full-year')}
        >
          Full Year
        </FilterButton>

        {periodType === 'month' && (
          <SelectContainer>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {monthNames.map((month, index) => (
                <option key={index} value={index}>
                  {month}
                </option>
              ))}
            </Select>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </SelectContainer>
        )}

        {periodType === 'half-year' && (
          <SelectContainer>
            <Select
              value={selectedHalf}
              onChange={(e) => setSelectedHalf(e.target.value as 'H1' | 'H2')}
            >
              <option value="H1">H1 (Jan-Jun)</option>
              <option value="H2">H2 (Jul-Dec)</option>
            </Select>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </SelectContainer>
        )}

        {periodType === 'full-year' && (
          <SelectContainer>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </SelectContainer>
        )}
      </FilterContainer>

      {loading ? (
        <LoadingContainer>
          <FiRefreshCw style={{ animation: 'spin 1s linear infinite', marginRight: theme.spacing[2] }} />
          Loading...
        </LoadingContainer>
      ) : profitLossData ? (
        <>
          <StatsContainer>
            <StatCard>
              <CardBody>
                <StatValue color={theme.colors.primary[600]}>
                  <FiDollarSign />
                  {formatCurrency(profitLossData.openingBalance)}
                </StatValue>
                <StatLabel>Opening Balance</StatLabel>
              </CardBody>
            </StatCard>

            <StatCard>
              <CardBody>
                <StatValue color={theme.colors.accent.success}>
                  <FiTrendingUp />
                  {formatCurrency(profitLossData.revenue)}
                </StatValue>
                <StatLabel>Revenue</StatLabel>
              </CardBody>
            </StatCard>

            <StatCard>
              <CardBody>
                <StatValue color={theme.colors.accent.danger}>
                  <FiTrendingDown />
                  {formatCurrency(profitLossData.expenses)}
                </StatValue>
                <StatLabel>Expenses</StatLabel>
              </CardBody>
            </StatCard>

            <StatCard>
              <CardBody>
                <StatValue color={theme.colors.primary[600]}>
                  <FiDollarSign />
                  {formatCurrency(profitLossData.closingBalance)}
                </StatValue>
                <StatLabel>Closing Balance</StatLabel>
              </CardBody>
            </StatCard>

            <StatCard>
              <CardBody>
                <StatValue color={profitLossData.netProfit >= 0 ? theme.colors.accent.success : theme.colors.accent.danger}>
                  {profitLossData.netProfit >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                  {formatCurrency(profitLossData.netProfit)}
                </StatValue>
                <StatLabel>Net Profit/Loss</StatLabel>
              </CardBody>
            </StatCard>
          </StatsContainer>

          <ReportCard>
            <CardBody>
              <h2 style={{ marginBottom: theme.spacing[4], color: theme.colors.secondary[900] }}>
                Profit & Loss Statement - {profitLossData.period}
              </h2>
              <ReportTable>
                <thead>
                  <tr>
                    <ReportHeader>Item</ReportHeader>
                    <ReportHeader align="right">Amount</ReportHeader>
                  </tr>
                </thead>
                <tbody>
                  <ReportRow>
                    <ReportCell>Opening Balance</ReportCell>
                    <ReportCell align="right">{formatCurrency(profitLossData.openingBalance)}</ReportCell>
                  </ReportRow>
                  <ReportRow>
                    <ReportCell>Revenue</ReportCell>
                    <ReportCell align="right" style={{ color: theme.colors.accent.success }}>
                      {formatCurrency(profitLossData.revenue)}
                    </ReportCell>
                  </ReportRow>
                  <ReportRow>
                    <ReportCell>Expenses</ReportCell>
                    <ReportCell align="right" style={{ color: theme.colors.accent.danger }}>
                      ({formatCurrency(profitLossData.expenses)})
                    </ReportCell>
                  </ReportRow>
                  <ReportRow isTotal>
                    <ReportCell>Net Profit/Loss</ReportCell>
                    <ReportCell align="right" style={{ 
                      color: profitLossData.netProfit >= 0 ? theme.colors.accent.success : theme.colors.accent.danger 
                    }}>
                      {formatCurrency(profitLossData.netProfit)}
                    </ReportCell>
                  </ReportRow>
                  <ReportRow isTotal>
                    <ReportCell>Closing Balance</ReportCell>
                    <ReportCell align="right">{formatCurrency(profitLossData.closingBalance)}</ReportCell>
                  </ReportRow>
                </tbody>
              </ReportTable>
            </CardBody>
          </ReportCard>

          <ReportCard>
            <CardBody>
              <h2 style={{ marginBottom: theme.spacing[4], color: theme.colors.secondary[900] }}>
                Account Receivable - {profitLossData.period}
              </h2>
              <ReportTable>
                <thead>
                  <tr>
                    <ReportHeader>Item</ReportHeader>
                    <ReportHeader align="right">Amount</ReportHeader>
                  </tr>
                </thead>
                <tbody>
                  <ReportRow>
                    <ReportCell>Opening Account Receivable</ReportCell>
                    <ReportCell align="right">{formatCurrency(profitLossData.accountReceivable.opening)}</ReportCell>
                  </ReportRow>
                  <ReportRow>
                    <ReportCell>Total Receivables</ReportCell>
                    <ReportCell align="right" style={{ color: theme.colors.accent.success }}>
                      {formatCurrency(profitLossData.accountReceivable.totalReceivables)}
                    </ReportCell>
                  </ReportRow>
                  <ReportRow>
                    <ReportCell>Received</ReportCell>
                    <ReportCell align="right" style={{ color: theme.colors.accent.success }}>
                      ({formatCurrency(profitLossData.accountReceivable.received)})
                    </ReportCell>
                  </ReportRow>
                  <ReportRow isTotal>
                    <ReportCell>Closing Account Receivable</ReportCell>
                    <ReportCell align="right">{formatCurrency(profitLossData.accountReceivable.closing)}</ReportCell>
                  </ReportRow>
                </tbody>
              </ReportTable>
            </CardBody>
          </ReportCard>

          <ReportCard>
            <CardBody>
              <h2 style={{ marginBottom: theme.spacing[4], color: theme.colors.secondary[900] }}>
                Account Payable - {profitLossData.period}
              </h2>
              <ReportTable>
                <thead>
                  <tr>
                    <ReportHeader>Item</ReportHeader>
                    <ReportHeader align="right">Amount</ReportHeader>
                  </tr>
                </thead>
                <tbody>
                  <ReportRow>
                    <ReportCell>Opening Account Payable</ReportCell>
                    <ReportCell align="right">{formatCurrency(profitLossData.accountPayable.opening)}</ReportCell>
                  </ReportRow>
                  <ReportRow>
                    <ReportCell>Total Payables</ReportCell>
                    <ReportCell align="right" style={{ color: theme.colors.accent.danger }}>
                      {formatCurrency(profitLossData.accountPayable.totalPayables)}
                    </ReportCell>
                  </ReportRow>
                  <ReportRow>
                    <ReportCell>Paid</ReportCell>
                    <ReportCell align="right" style={{ color: theme.colors.accent.success }}>
                      ({formatCurrency(profitLossData.accountPayable.paid)})
                    </ReportCell>
                  </ReportRow>
                  <ReportRow isTotal>
                    <ReportCell>Closing Account Payable</ReportCell>
                    <ReportCell align="right">{formatCurrency(profitLossData.accountPayable.closing)}</ReportCell>
                  </ReportRow>
                </tbody>
              </ReportTable>
            </CardBody>
          </ReportCard>
        </>
      ) : null}
      </ScrollableContainer>
    </DashboardLayout>
  );
};

export default ProfitLossPage;
